import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import { Label, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import cx from "classnames";
import { ProjectStructure } from "components/_Modals/ProductAddingModal/ProjectStructure";
import { ProjectDetailsModal } from "components/_Modals/ProjectDetailsModal/ProjectDetailsModal";
import { Button } from "components/Button";
import { ProductItem } from "components/Product/ProductItem";
import { ModalWrapper } from "components/ModalWrapper";
import { ProjectCreateMut, SameProjectCreateMut } from "api/mutations";
import {
  ProductAddingModalContextProvider,
  useAppBaseContext,
} from "providers";
import { useTranslate } from "hooks";
import { useChangeProject, useRestoreProject } from "hooks/projectHooks";
import {
  useAddedProductStore,
  useCopiedItemStore,
  useProjectDataStore,
} from "stores/projectStore";
import { prepareNodesForProductWithAccessories } from "utils";
import { useFilteredProjects } from "hooks/productHooks/useFilteredProjects";
import { ProjectSelect } from "./ProjectSelect";
import styles from "./ProjectAddingModal.module.scss";
import { useFetchProjectList } from "./hooks/useFetchProjectList";

export const ProductAddingModal = NiceModal.create(
  Scrivito.connect(({ productImage, requestParams, accessoriesData }) => {
    const { isSameProj } = useAppBaseContext();
    const translate = useTranslate();
    const setCopiedItem = useCopiedItemStore((state) => state.setCopiedItem);
    const projectId = useProjectDataStore((state) => state.projectId);
    const hasAddedNode = useAddedProductStore((state) => state.hasAddedNode);
    const resetAddedProducts = useAddedProductStore((state) => state.reset);
    const fetchProjectTreeError = useProjectDataStore((state) => state.error);
    const setFetchProjectTreeError = useProjectDataStore(
      (state) => state.setError,
    );
    const handleChangeProject = useChangeProject();
    const modal = NiceModal.useModal();
    const [quantity, setQuantity] = useState(1);

    const [addedProducts, setAddedProducts] = useState([]);

    const [{ data, fetching, stale }, exeGetProjectsOptions] =
      useFetchProjectList();

    const { items } = data?.currentUser?.projectList || [];

    const filteredProjects =
      isSameProj &&
      requestParams?.kind !== "CUSTOM_PRODUCT" &&
      useFilteredProjects(items, requestParams);

    useRestoreProject();

    useEffect(() => {
      const newCopiedItem = prepareNodesForProductWithAccessories({
        quantity,
        accessoriesData,
        requestParams,
      });
      setCopiedItem(newCopiedItem);

      return () => {
        setCopiedItem(null);
        resetAddedProducts();
      };
    }, [
      quantity,
      requestParams,
      setCopiedItem,
      resetAddedProducts,
      accessoriesData,
    ]);

    const options = useMemo(() => {
      const projectsInDropdown =
        isSameProj && requestParams?.kind !== "CUSTOM_PRODUCT"
          ? filteredProjects
          : items;

      return (
        projectsInDropdown?.filter(Boolean).map((item) => ({
          value: item.id,
          label: item.title,
        })) || []
      );
    }, [isSameProj, requestParams?.kind, filteredProjects, items]);

    const handleOptionChange = useCallback(
      (option) => {
        if (projectId === option.value) {
          return;
        }

        const newUrl = new URL(window.location.href);
        const originalProjectId = newUrl.searchParams.get("originalProjectId");

        if (!originalProjectId) {
          newUrl.searchParams.set("originalProjectId", option.value);
          newUrl.searchParams.set("originalProjectName", option.label);
          window.history.replaceState(null, "", newUrl.toString());
        }

        handleChangeProject(option.value);
      },
      [projectId, handleChangeProject],
    );

    const selectedOption = useMemo(
      () => options?.find((option) => option.value === projectId),
      [options, projectId],
    );

    const toggleModal = useCallback(() => {
      NiceModal.show(ProjectDetailsModal, {
        mutation: isSameProj ? SameProjectCreateMut : ProjectCreateMut,
        requestParams,
      }).then(({ id, title }) => {
        exeGetProjectsOptions();
        handleOptionChange({ value: id, label: title });
      });
    }, [exeGetProjectsOptions, handleOptionChange, isSameProj, requestParams]);

    const handleQuantityUpdate = (value) => {
      const parsedValue = Number(value);
      if (isNaN(parsedValue) || parsedValue <= 0) {
        setQuantity(1);
      } else {
        setQuantity(parsedValue);
      }
    };

    const handleCloseModal = useCallback(() => {
      resetAddedProducts();
      modal.remove();
    }, [modal, resetAddedProducts]);

    useEffect(() => {
      if (fetchProjectTreeError) {
        handleCloseModal();
        setFetchProjectTreeError(null);
      }
    }, [
      modal,
      handleCloseModal,
      setFetchProjectTreeError,
      fetchProjectTreeError,
    ]);

    return (
      <ProductAddingModalContextProvider
        value={{ addedProducts, setAddedProducts }}
      >
        <ModalWrapper
          isOpen={modal.visible}
          centered={true}
          size="xl"
          className={styles.modalWrapper}
          toggle={modal.remove}
          onClosed={handleCloseModal}
          isLoading={fetching || stale}
        >
          <ModalHeader
            toggle={modal.hide}
            tag="div"
            className="pb-0 d-flex align-items-start"
          >
            <h3 className="text-uppercase mb-1">
              {translate("ADD_TO_PROJECT")}
            </h3>
            <h6 className="mb-0 text-break">{requestParams.productName}</h6>
          </ModalHeader>
          <ModalBody className={cx("pt-3 row d-flex", styles.bodyHeight)}>
            <div className="col-lg-6 col-12 d-flex">
              <div className="border rounded-1 mr-2 px-3 py-4 w-100">
                <h5 className="mb-1">
                  {translate("ADD_PRODUCT_MODAL_PRODUCT_TITLE")}
                </h5>
                <span className="text-gray-500">
                  {translate("ADD_PRODUCT_MODAL_PRODUCT_SUBTITLE")}
                </span>
                <div className="mt-5 h-100">
                  <ProductItem
                    title={requestParams.productName}
                    quantity={quantity}
                    withIcons={false}
                    quantityTitle={translate("QUANTITY")}
                    presentationTitle={translate("PRESENTATION_ORDER_CODE")}
                    isEditMode={true}
                    handleQuantityUpdate={handleQuantityUpdate}
                    presentation={requestParams.presentation}
                    productImage={productImage}
                    allowProductInputOutsidePropagation={false}
                    accessoriesData={accessoriesData}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-12 d-flex flex-column h-100">
              <div className="border rounded-1 mr-2 d-flex flex-column px-3 pt-4 w-100 h-100">
                <div className="mb-5_5 d-flex flex-shrink-0 align-items-end justify-content-between">
                  <div>
                    <Label
                      className={cx(
                        "d-flex align-items-center justify-content-between mb-1 fs-16 fw-bolder text-gray-700 noTextSelection",
                        styles.noselect,
                      )}
                      for="productSelect"
                    >
                      {translate("ADD_PRODUCT_MODAL_SELECT_LABEL")}
                    </Label>
                    <div className="d-flex justify-content-between align-items-center">
                      <div
                        className="d-flex flex-column flex-grow-1"
                        style={{ width: "300px" }}
                      >
                        <ProjectSelect
                          {...{ handleOptionChange, options, selectedOption }}
                        />
                      </div>
                    </div>
                  </div>
                  <span className="pb-2">
                    {translate("ADD_PRODUCT_MODAL_OR")}
                  </span>
                  <Button
                    data-testid="ProductAddingModal_create-new-project-btn"
                    color="secondary"
                    onClick={toggleModal}
                  >
                    {translate("ADD_PRODUCT_MODAL_CREATE_NEW_PROJECT")}
                  </Button>
                </div>
                {projectId && selectedOption && (
                  <ProjectStructure projectTitle={selectedOption.label} />
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="pt-0">
            <Button onClick={handleCloseModal} disabled={!hasAddedNode}>
              {translate("PRODUCT_CONFIGURATOR_DONE")}
            </Button>
          </ModalFooter>
        </ModalWrapper>
      </ProductAddingModalContextProvider>
    );
  }),
);
